import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import MobileLayout from "../../components/General/MobileLayout/MobileLayout";
import { useFoodManagement } from "../../hooks/useFoodManagement";
import { FoodItem as ApiFoodItem } from "../../services/foodApiService";
import ImageDosa from "../../assets/food.png";
import FlameIcon from "../../assets/Flame1.svg";
import ClockIcon from "../../assets/path31836.png";

const FoodNutritionPage = () => {
  const navigate = useNavigate();
  const { uploadedImage, foodItemsFromBackend } = useFoodManagement();
  const [selectedItem, setSelectedItem] = useState(0);

  const convertApiFoodItems = (apiItems: ApiFoodItem[]) => {
    return apiItems.map((item, index) => ({
      id: index + 1,
      name: item.name,
      image: uploadedImage ?? ImageDosa,
      calories: item.calorie_content_kcal,
      weight: item.serving_size_g,
      servings: 1,
      nutrition: {
        totalCalories: item.calorie_content_kcal,
        totalFats: item.fat_content_g,
        transFats: item.trans_fat_g,
        saturatedFats: item.saturated_fat_g,
        totalCarbs: item.carbohydrate_content_g,
        netCarbs: item.net_carbs_g,
        dietaryFibers: item.dietary_fiber_g,
        totalGI: item.GI_estimate,
        totalGL: item.GL_estimate,
        proteins: item.protein_content_g,
      },
    }));
  };

  // Use actual API data or empty array - no hardcoded fallback
  const foodItems =
    foodItemsFromBackend && foodItemsFromBackend.length > 0
      ? convertApiFoodItems(foodItemsFromBackend)
      : [];

  // If no food items, show empty state message
  if (foodItems.length === 0) {
    return (
      <MobileLayout>
        <div className="scrollbar-hide mx-auto flex min-h-screen w-full max-w-[400px] items-center justify-center bg-background">
          <div className="p-6 text-center">
            <button
              onClick={() => navigate(-1)}
              className="mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-muted/80"
            >
              <ArrowLeftOutlined className="text-title3" />
            </button>
            <p className="text-title3 font-semibold text-foreground">
              No food data available
            </p>
            <p className="mt-2 text-subhead text-muted-foreground">
              Please scan or add food items first
            </p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  const currentItem = foodItems[selectedItem];

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MobileLayout>
      <div className="scrollbar-hide mx-auto min-h-screen w-full max-w-[400px] overflow-auto bg-background">
        {/* Food Image */}
        <div className="relative">
          <img
            src={uploadedImage ?? ImageDosa}
            alt={currentItem.name}
            className="h-64 w-full object-cover"
          />
          <button
            onClick={handleBack}
            className="absolute left-4 top-4 rounded-full bg-black bg-opacity-50 p-2 text-white"
          >
            <ArrowLeftOutlined className="text-title3" />
          </button>
        </div>

        {/* Nutrition Info Card */}
        <div className="mx-4 mt-4 rounded-xl bg-[#2F312E] p-4">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-full bg-[#C6F68D] p-2">
              <svg
                className="h-6 w-6 text-[#171916]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" />
              </svg>
            </div>
            <div>
              <h2 className="text-title3 font-semibold text-white">
                Total Calories
              </h2>
              <p className="text-large-title font-bold text-white">
                {currentItem.nutrition.totalCalories}
              </p>
            </div>
          </div>

          {/* Nutrition Facts */}
          <div className="space-y-3">
            <h3 className="mb-3 text-title3 font-semibold text-white">
              Nutrition Facts
            </h3>

            <div className="flex justify-between text-white">
              <span>Calories</span>
              <div className="text-right">
                <span className="font-semibold">
                  {currentItem.nutrition.totalCalories} Cal
                </span>
                <div className="text-caption1 text-muted-foreground">
                  % Daily Value
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-white">
                <span className="font-semibold">Total Fats</span>
                <span>{currentItem.nutrition.totalFats}g</span>
              </div>
              <div className="flex justify-between pl-4 text-subhead text-white">
                <span>Trans Fats</span>
                <span>{currentItem.nutrition.transFats}g</span>
              </div>
              <div className="flex justify-between pl-4 text-subhead text-white">
                <span>Saturated Fats</span>
                <span>{currentItem.nutrition.saturatedFats}g</span>
              </div>
            </div>

            <div className="flex justify-between text-white">
              <span className="font-semibold">Total Carbohydrates</span>
              <span>{currentItem.nutrition.totalCarbs}g</span>
            </div>
            <div className="flex justify-between pl-4 text-subhead text-white">
              <span>Net Carbs</span>
              <span>{currentItem.nutrition.netCarbs.toFixed(1)}g</span>
            </div>
            <div className="flex justify-between pl-4 text-subhead text-white">
              <span>Dietary Fibers</span>
              <span>{currentItem.nutrition.dietaryFibers.toFixed(1)}g</span>
            </div>

            <div className="flex justify-between text-white">
              <span className="font-semibold">Glycemic Index (GI)</span>
              <span className="font-semibold text-[#C6F68D]">
                {currentItem.nutrition.totalGI}
              </span>
            </div>

            <div className="flex justify-between text-white">
              <span className="font-semibold">Glycemic Load (GL)</span>
              <span className="font-semibold text-[#C6F68D]">
                {typeof currentItem.nutrition.totalGL === "number"
                  ? currentItem.nutrition.totalGL.toFixed(1)
                  : currentItem.nutrition.totalGL}
              </span>
            </div>

            <div className="flex justify-between text-white">
              <span className="font-semibold">Proteins</span>
              <span>{currentItem.nutrition.proteins}g</span>
            </div>
          </div>
        </div>

        {/* Food Items List */}
        <div className="mx-4 mb-6 mt-4 space-y-3">
          {foodItems.map((item, index) => (
            <div
              key={item.id}
              className={`cursor-pointer rounded-xl bg-[#2F312E] p-4 transition-colors ${
                selectedItem === index ? "ring-2 ring-[#C6F68D]" : ""
              }`}
              onClick={() => setSelectedItem(index)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={uploadedImage ?? ImageDosa}
                    alt={item.name}
                    className="h-12 w-12 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="text-callout font-semibold text-white">
                      {item.name}
                    </h3>
                    <div className="mt-1 flex items-center gap-4 text-subhead text-white">
                      <div className="flex items-center gap-1">
                        <img
                          src={FlameIcon}
                          alt="Calories"
                          className="h-4 w-4 object-contain"
                        />
                        <span>{item.calories}cal</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <img
                          src={ClockIcon}
                          alt="Weight"
                          className="h-4 w-4 object-contain"
                        />
                        <span>{item.weight}g</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <img
                          src={ClockIcon}
                          alt="Serving"
                          className="h-4 w-4 object-contain"
                        />
                        <span>{item.servings} Serving</span>
                      </div>
                    </div>
                  </div>
                </div>
                {selectedItem === index && (
                  <div className="h-3 w-3 rounded-full bg-[#C6F68D]" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </MobileLayout>
  );
};

export default FoodNutritionPage;
